/**
 * Scoring Matrix Print Page
 *
 * Print-optimized page showing all tasks and competencies with their scoring criteria.
 * Each item is shown as a row with columns for each score level (1-5).
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PrintLayout } from '../../components/print';
import { competenciesApi, type ScoringMatrix, type CompetencyMatrixItem, type TaskMatrixItem } from '../../lib/competenciesApi';

const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

interface CompetencyTableProps {
  title: string;
  competencies: CompetencyMatrixItem[];
  colorClass: string;
}

function CompetencyTable({ title, competencies, colorClass }: CompetencyTableProps) {
  if (competencies.length === 0) {
    return null;
  }

  return (
    <div className="print-section mb-8">
      <h2 className={`print-section-header text-lg font-bold mb-4 ${colorClass}`}>
        {title}
      </h2>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2 text-left w-32 font-semibold">
              Competentie
            </th>
            {[1, 2, 3, 4, 5].map((score) => (
              <th key={score} className="border border-gray-300 p-2 text-center font-semibold">
                <div className="font-bold">{score}</div>
                <div className="text-gray-600 font-normal">{SCORE_LABELS[score]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {competencies.map((comp, index) => (
            <tr key={comp.id} className={`print-row ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="border border-gray-300 p-2 align-top">
                <div className="font-semibold text-gray-900">{comp.name}</div>
                {comp.beschrijving && (
                  <div className="text-gray-500 mt-1 text-xs leading-tight">
                    {comp.beschrijving}
                  </div>
                )}
              </td>
              {[1, 2, 3, 4, 5].map((score) => {
                const criterion = comp.criteria.find(c => c.scoreLevel === score);
                return (
                  <td key={score} className="border border-gray-300 p-2 align-top">
                    {criterion ? (
                      <div className="text-gray-700 leading-tight">
                        {criterion.description}
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">-</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TaskTableProps {
  tasks: TaskMatrixItem[];
}

function TaskTable({ tasks }: TaskTableProps) {
  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="print-section mb-8">
      <h2 className="print-section-header text-lg font-bold mb-4 text-green-800">
        Taken
      </h2>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2 text-left w-32 font-semibold">
              Taak
            </th>
            <th className="border border-gray-300 p-2 text-center w-16 font-semibold">
              Weging
            </th>
            {[1, 2, 3, 4, 5].map((score) => (
              <th key={score} className="border border-gray-300 p-2 text-center font-semibold">
                <div className="font-bold">{score}</div>
                <div className="text-gray-600 font-normal">{SCORE_LABELS[score]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tasks.map((task, index) => (
            <tr key={task.id} className={`print-row ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="border border-gray-300 p-2 align-top">
                <div className="font-semibold text-gray-900">{task.name}</div>
                {task.beschrijving && (
                  <div className="text-gray-500 mt-1 text-xs leading-tight">
                    {task.beschrijving}
                  </div>
                )}
              </td>
              <td className="border border-gray-300 p-2 text-center font-medium">
                {task.weging}
              </td>
              {[1, 2, 3, 4, 5].map((score) => {
                const criterion = task.criteria.find(c => c.scoreLevel === score);
                return (
                  <td key={score} className="border border-gray-300 p-2 align-top">
                    {criterion ? (
                      <div className="text-gray-700 leading-tight">
                        {criterion.description}
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">-</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CompetencyMatrixPrintPage() {
  const navigate = useNavigate();

  const { data: matrix, isLoading, error } = useQuery<ScoringMatrix>({
    queryKey: ['scoring', 'matrix'],
    queryFn: () => competenciesApi.getScoringMatrix(),
  });

  const handleClose = () => {
    // Try to close the window, if that fails navigate back
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const totalItems = matrix
    ? matrix.tasks.length + matrix.competencies.basis.length + matrix.competencies.vak.length
    : 0;

  return (
    <PrintLayout
      title="Scoringsmatrix"
      subtitle="Scoringscriteria per taak en competentie"
      isLoading={isLoading}
      loadingMessage="Scoringsmatrix voorbereiden..."
      error={error ? 'Kon de scoringsmatrix niet laden' : undefined}
      onClose={handleClose}
      orientation="landscape"
      footerContent={
        <p>
          Functieloonhuis Beoordelingssysteem - Scoringsmatrix
          {matrix && ` - Gegenereerd: ${new Date(matrix.generatedAt).toLocaleString('nl-NL')}`}
        </p>
      }
    >
      {matrix && (
        <>
          {/* Score Legend */}
          <div className="bg-blue-50 rounded-lg p-3 mb-6 no-print">
            <p className="text-xs font-medium text-blue-800 mb-2">Scorelegenda:</p>
            <div className="flex gap-4 text-xs">
              {[1, 2, 3, 4, 5].map((score) => (
                <span key={score} className="flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center text-xs font-bold">
                    {score}
                  </span>
                  <span className="text-gray-600">{SCORE_LABELS[score]}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Tasks */}
          <TaskTable tasks={matrix.tasks} />

          {/* Basis Competencies */}
          <CompetencyTable
            title="Basiscompetenties"
            competencies={matrix.competencies.basis}
            colorClass="text-purple-800"
          />

          {/* Vak Competencies */}
          <CompetencyTable
            title="Vakcompetenties"
            competencies={matrix.competencies.vak}
            colorClass="text-indigo-800"
          />

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-600">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <span className="font-medium">Taken:</span>{' '}
                {matrix.tasks.length}
              </div>
              <div>
                <span className="font-medium">Basiscompetenties:</span>{' '}
                {matrix.competencies.basis.length}
              </div>
              <div>
                <span className="font-medium">Vakcompetenties:</span>{' '}
                {matrix.competencies.vak.length}
              </div>
              <div>
                <span className="font-medium">Totaal:</span>{' '}
                {totalItems} items
              </div>
            </div>
          </div>
        </>
      )}
    </PrintLayout>
  );
}

export default CompetencyMatrixPrintPage;
